// frontend/src/App/utils/redirectHelpers.tsx
import { Navigate, NavigateFunction } from 'react-router-dom';
import { Area } from '../../types/common.types';

/**
 * Tipo para parámetro de área (puede ser single, array o null)
 */
type AreaInput = Area | Area[] | null;

/**
 * Helper: Convierte area a array normalizado
 */
const normalizeArea = (area: AreaInput): Area[] => {
	if (Array.isArray(area)) return area;
	if (area) return [area];
	return [];
};

/**
 * Helper: Determina la ruta basada en el área del usuario
 */
const getRouteForArea = (areaArray: Area[]): string => {
	if (areaArray.includes('SuperAdmin' as Area)) {
		return '/superadmin/dashboard';
	}
	if (areaArray.includes('System' as Area)) {
		return '/system-dashboard';
	}
	if (areaArray.includes('Extraccion' as Area)) {
		return '/extraccion';
	}
	return '/casos';
};

/**
 * Navega imperativamente basándose en el área del usuario
 * Útil para login, callbacks, etc.
 *
 * @param area - Área del usuario (puede ser array o string)
 * @param navigate - Función navigate de react-router-dom
 *
 * @example
 * const navigate = useNavigate();
 * navigateBasedOnArea(user.area, navigate);
 */
export const navigateBasedOnArea = (
	area: AreaInput,
	navigate: NavigateFunction
): void => {
	const areaArray = normalizeArea(area);
	const route = getRouteForArea(areaArray);
	navigate(route);
};

/**
 * Determina la redirección basada en el área del usuario (JSX)
 * Útil para componentes de rutas protegidas
 *
 * @param area - Área del usuario (puede ser un array o string)
 * @returns Elemento Navigate de React Router
 *
 * @example
 * return getAreaBasedRedirect(user.area);
 */
export const getAreaBasedRedirect = (area: AreaInput): React.ReactElement => {
	const areaArray = normalizeArea(area);
	const route = getRouteForArea(areaArray);
	return <Navigate to={route} />;
};

/**
 * Maneja la redirección según el estado de autenticación y área
 * @param isLoggedIn - Estado de autenticación del usuario
 * @param area - Área del usuario
 * @returns Elemento Navigate de React Router
 */
export const getAuthBasedRedirect = (
	isLoggedIn: boolean,
	area: AreaInput
): React.ReactElement => {
	if (!isLoggedIn) {
		return <Navigate to='/' />;
	}
	return getAreaBasedRedirect(area);
};
